// Page docs (structure + permissions + flows): pages/README.md
// pages/_app.tsx
import { useEffect, useMemo, useState } from "react";
import { ThemeProvider, createTheme, StyledEngineProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
// NOTE: We also provide the legacy @mui/styles ThemeProvider so makeStyles receives a theme during SSR
import StylesThemeProvider from "@mui/styles/ThemeProvider";
import CookieConsentBar from "@/components/consent/CookieConsent";
import { Alert, Snackbar } from "@mui/material";
import React from "react";
// Global styles aligned with develop branch
import "../styles/scss/nextjs-material-kit-pro.scss";
import "../styles/css/react-demo.css";
import "animate.css/animate.min.css";
import { Provider } from "react-redux";
import { store } from "@/src/store";
import { AuthProvider } from "@/context/AuthContext";
import { SiteSettingsProvider } from "@/context/SiteSettingsContext";
import { common } from "@/theme/colors";
import { createFontSizeCssVariables, createTypography, fontSizes } from "@/theme/typography";
import { useBootstrap } from "@/src/hooks/useBootstrap";
import { useAppDispatch, useAppSelector } from "@/src/store/hooks";
import { useRouter } from "next/router";
import AccessGateModal from "@/components/Auth/AccessGateModal";
import TrafficTrackerPill from "@/components/Analytics/TrafficTrackerPill";
import { initGlobalAnalyticsCapture, noteAnalyticsRouteChange } from "@/utils/analyticsBeacon";
import { trackAuthStep } from "@/utils/analyticsAggregates";
import { selectFlashQueue, shiftFlash } from "@/src/store/features/flashSlice";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export default function App(props) {
  return /*#__PURE__*/_jsx(Provider, {
    store: store,
    children: /*#__PURE__*/_jsx(AppProviders, {
      ...props
    })
  });
}
function AppProviders({
  Component,
  pageProps
}) {
  useBootstrap();
  const router = useRouter();

  // Passive global analytics capture (session source, gate funnel, scroll
  // depth, web vitals). Everything drops when the tracking setting is off.
  useEffect(() => {
    initGlobalAnalyticsCapture();
    const onRoute = url => noteAnalyticsRouteChange(url.split("?")[0]);
    router.events.on("routeChangeComplete", onRoute);
    return () => router.events.off("routeChangeComplete", onRoute);
  }, [router.events]);
  const [accessGate, setAccessGate] = useState(null);

  // Single theme instance for the whole app
  const theme = useMemo(() => {
    const baseTheme = createTheme({
      components: {
        MuiSelect: {
          defaultProps: {
            variant: "standard"
          }
        },
        MuiTab: {
          styleOverrides: {
            root: {
              "&.Mui-selected": {
                color: `${common.white} !important`
              }
            }
          }
        }
      }
    });
    return createTheme(baseTheme, {
      fontSizes,
      typography: createTypography(baseTheme),
      components: {
        ...baseTheme.components,
        MuiCssBaseline: {
          styleOverrides: {
            ...createFontSizeCssVariables(baseTheme),
            body: {
              fontSize: "var(--pc-font-body1)"
            },
            "h1, h2, h3, h4, h5, h6": {
              fontWeight: 600,
              overflowWrap: "anywhere",
              wordBreak: "break-word"
            },
            ".MuiTypography-h1, .MuiTypography-h2, .MuiTypography-h3, .MuiTypography-h4, .MuiTypography-h5, .MuiTypography-h6": {
              overflowWrap: "anywhere",
              wordBreak: "break-word"
            }
          }
        },
        MuiChip: {
          styleOverrides: {
            root: {
              maxWidth: "100%"
            },
            label: {
              whiteSpace: "normal",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }
          }
        }
      }
    });
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const wanted = sessionStorage.getItem("redirect");
      if (wanted) {
        sessionStorage.removeItem("redirect");
        // Use a direct location replace to avoid requiring NextRouter during prerender/export
        window.location.replace(wanted);
      }
    } catch {
      /* no-op */
    }
  }, []);

  // Register service worker for image & static asset caching
  useEffect(() => {
    if (typeof window !== "undefined" && "serviceWorker" in navigator && process.env.NODE_ENV === "production") {
      navigator.serviceWorker.register("/sw.js").catch(() => {
        /* SW registration failed — non-critical */
      });
    }
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = event => {
      const detail = event.detail;
      if (detail?.reason) {
        setAccessGate(detail);
      }
    };
    window.addEventListener("pc-access-gate", handler);
    return () => {
      window.removeEventListener("pc-access-gate", handler);
    };
  }, []);
  const handleAccessGateCancel = () => setAccessGate(null);
  const handleAccessGateConfirm = () => {
    if (!accessGate) return;
    trackAuthStep("gate_passed");
    if (accessGate.reason === "insufficient-role") {
      void router.push("/contact-us");
    } else {
      const query = accessGate.returnUrl ? {
        returnUrl: accessGate.returnUrl
      } : undefined;
      void router.push({
        pathname: "/login",
        query
      });
    }
    setAccessGate(null);
  };
  return /*#__PURE__*/_jsx(AuthProvider, {
    children: /*#__PURE__*/_jsx(SiteSettingsProvider, {
      children: /*#__PURE__*/_jsx(ThemeProvider, {
        theme: theme,
        children: /*#__PURE__*/_jsx(StylesThemeProvider, {
          theme: theme,
          children: /*#__PURE__*/_jsxs(StyledEngineProvider, {
            injectFirst: true,
            children: [/*#__PURE__*/_jsx(CssBaseline, {}), /*#__PURE__*/_jsx(CookieConsentBar, {}), /*#__PURE__*/_jsx(Component, {
              ...pageProps
            }), /*#__PURE__*/_jsx(AccessGateModal, {
              open: Boolean(accessGate),
              reason: accessGate?.reason,
              onCancel: handleAccessGateCancel,
              onConfirm: handleAccessGateConfirm
            }), /*#__PURE__*/_jsx(FlashSnackbar, {}), /*#__PURE__*/_jsx(TrafficTrackerPill, {})]
          })
        })
      })
    })
  });
}
function FlashSnackbar() {
  const queue = useAppSelector(selectFlashQueue);
  const dispatch = useAppDispatch();
  const current = queue[0] ?? null;
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (current) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [current?.id]);
  const handleClose = (_event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
    if (current) {
      dispatch(shiftFlash());
    }
  };
  return /*#__PURE__*/_jsx(Snackbar, {
    open: Boolean(current) && open,
    autoHideDuration: 5000,
    onClose: handleClose,
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "center"
    },
    children: /*#__PURE__*/_jsx(Alert, {
      severity: current?.severity ?? "info",
      onClose: handleClose,
      sx: {
        width: "100%"
      },
      children: current?.message ?? ""
    })
  });
}